import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { format, parse } from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUsageHistoryDetailsQuery } from "../../services/query/project-asset-details.query";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import APIError from "../APIError/APIError";
import { BarChart } from "../Charts/BarChart";
/** Convert backend date labels to the Figma's "dd/MM" x-axis format. */
const toMonthDayLabel = (label) => {
    const parsed = /^\d{4}-\d{2}-\d{2}/.test(label) ? new Date(label) : parse(label, "dd-MMM", new Date());
    return Number.isNaN(parsed.getTime()) ? label : format(parsed, "dd/MM");
};
/** Charge/Discharge traces + layout shared by every Energy Use History card, so the ESS and
 *  machine drawers render an identical chart from their own data sources. */
export const buildEnergyChartData = (dates, charge, discharge) => ({
    config: { displayModeBar: false, staticPlot: false },
    data: [
        {
            hoverinfo: "none",
            hoverlabel: { bgcolor: "#1AC69E", font: { color: "#FFFFFF" } },
            hovertemplate: "%{y}",
            marker: { color: "#1AC69E" },
            name: "",
            type: "bar",
            x: dates,
            y: charge
        },
        {
            hoverinfo: "none",
            hoverlabel: { bgcolor: "#FB5A00", font: { color: "#FFFFFF" } },
            hovertemplate: "%{y}",
            marker: { color: "#FB5A00" },
            name: "",
            type: "bar",
            x: dates,
            y: discharge
        }
    ],
    layout: {
        autosize: true,
        barcornerradius: 15,
        height: 450,
        margin: { b: 50, l: 75, pad: 0, r: 10, t: 10 },
        responsive: true,
        showlegend: false,
        textangle: 0,
        xaxis: {
            tickangle: 0,
            tickfont: { color: "#666565", family: "Noto Sans, sans-serif", size: 14 },
            type: "category"
        },
        yaxis: {
            tickfont: { color: "#666565", family: "Noto Sans, sans-serif", size: 14 },
            ticksuffix: " kWh"
        }
    }
});
/** Number of most-recent days to display in the Energy Use History chart. */
const MAX_DISPLAY_DAYS = 30;
/** Minimum horizontal px per date; below this the plot expands and a bottom scrollbar appears (mirrors Energy Output History). */
const CHART_PX_PER_DATE = 50;
/** Figma bar width (px); expressed as a fraction of CHART_PX_PER_DATE since Plotly bar width on a category axis is in category-slot units. */
const BAR_WIDTH_RATIO = 9 / CHART_PX_PER_DATE;
/** Figma card width used as the fallback plot width until the container is measured (e.g., in tests). */
const CHART_FALLBACK_WIDTH = 1248;
/** Plotly plot height tuned to fit inside the Figma 452px card (header + legend + padding). */
const CHART_HEIGHT = 275;
/** Static Charge/Discharge legend (hoisted to module scope so it isn't redefined every render). */
const EnergyUseLegend = () => {
    const legendItems = [
        { color: "bg-[#1AC69E]", label: "Charge" },
        { color: "bg-[#FB5A00]", label: "Discharge" }
    ];
    return (_jsxs("div", { className: "flex w-full items-center justify-center", "data-test": "energy-use-legend", children: [_jsx("span", { className: "mr-2 align-bottom text-[12px] font-normal leading-[16px] text-[#666565]", children: "Legend:" }), _jsx("span", { className: "flex flex-wrap", children: legendItems.map((item, index) => (_jsxs("div", { className: "flex items-center mr-4 whitespace-nowrap", "data-test": `energy-use-legend-item-${index}`, children: [_jsx("div", { className: `w-2.5 h-2.5 rounded-full mr-1 ${item.color}` }), _jsx("span", { className: "align-bottom text-[14px] font-semibold leading-[20px] text-[#3F3F3F]", children: item.label })] }, item.label))) })] }));
};
const EnergyUseHistory = ({ assetId, dailyEnergyChartData: externalChartData, emptyContent, isLoading: externalIsLoading, isQueryEnabled = false, projectId, startDate, totalDischarge: externalTotalDischarge }) => {
    // Internal data fetching mode — when assetId/projectId/startDate are provided
    const { assetDetails } = useProjectDetailsStore((state) => ({
        assetDetails: state.assetDetails
    }));
    const assetSourceType = assetDetails?.assetSourceType || "";
    const hasInternalDataSource = !!assetId && !!projectId && !!startDate;
    const { data: usageHistoryData, isError: isUsageHistoryDetailsError, refetch: refetchUsageHistoryDetails, status: usageHistoryDetailsApiStatus } = useUsageHistoryDetailsQuery({
        aggregation: "daywiseMonth",
        assetId: assetId || "",
        assetSourceType,
        fetchHistoryData: hasInternalDataSource && isQueryEnabled,
        id: projectId || "",
        startDate: startDate || ""
    });
    const { internalChartData, internalTotalDischarge } = useMemo(() => {
        if (!usageHistoryData)
            return { internalChartData: null, internalTotalDischarge: 0 };
        const rawDaily = usageHistoryData?.energyHistogram?.dailyWiseMonth ?? [];
        const displayDaily = [...rawDaily]
            .sort((left, right) => (left.date ?? "").localeCompare(right.date ?? ""))
            .slice(-MAX_DISPLAY_DAYS);
        const displayDates = displayDaily.map((usage) => usage.date ?? "");
        const displayCharge = displayDaily.map((usage) => Math.round(usage.chargeEnergy ?? 0));
        const displayDischarge = displayDaily.map((usage) => Math.round(usage.dischargeEnergy ?? 0));
        // Sum the raw (unrounded) per-day discharge kWh for the same window and round only once for
        // display — summing the already-rounded per-day bars drifts the total (e.g. 0.4 + 0.4 → 0).
        const displayTotalDischarge = displayDaily.reduce((sum, usage) => sum + (typeof usage?.dischargeEnergy === "number" ? usage.dischargeEnergy : 0), 0);
        // Treat an all-zero / empty response as "no data" so the empty state shows instead of a
        // blank chart with only gridlines (the API returns 30 days of zeros for assets with none).
        const hasMeaningfulData = displayDates.length > 0 &&
            (displayCharge.some((value) => value > 0) || displayDischarge.some((value) => value > 0));
        if (!hasMeaningfulData)
            return { internalChartData: null, internalTotalDischarge: 0 };
        const monthDayDates = displayDates.map(toMonthDayLabel);
        return {
            internalChartData: buildEnergyChartData(monthDayDates, displayCharge, displayDischarge),
            internalTotalDischarge: displayTotalDischarge
        };
    }, [usageHistoryData]);
    // Resolve which data source to use
    const dailyEnergyChartData = hasInternalDataSource ? internalChartData : (externalChartData ?? null);
    const isError = hasInternalDataSource && isUsageHistoryDetailsError;
    const isLoading = hasInternalDataSource ? usageHistoryDetailsApiStatus === "pending" : (externalIsLoading ?? false);
    const totalDischarge = hasInternalDataSource ? internalTotalDischarge : (externalTotalDischarge ?? 0);
    // Track the available width so the plot can fill the container when zoomed out
    // and only overflow (scroll) when the dates can't fit (mirrors Energy Output History).
    const chartAreaRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
        const el = chartAreaRef.current;
        if (!el || typeof ResizeObserver === "undefined")
            return;
        const observer = new ResizeObserver((entries) => setContainerWidth(entries[0]?.contentRect.width ?? 0));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const dateCount = dailyEnergyChartData?.data?.[0]?.x?.length ?? 0;
    const contentWidth = dateCount * CHART_PX_PER_DATE;
    const availableWidth = containerWidth || CHART_FALLBACK_WIDTH;
    const shouldScroll = contentWidth > availableWidth;
    // Grow to fill the container when there's room; overflow into a scrollbar otherwise.
    const plotWidth = Math.max(contentWidth, availableWidth);
    const modifiedChartData = dailyEnergyChartData
        ? {
            ...dailyEnergyChartData,
            // Give each bar the Figma 9px width and offset them so the pair touches, centered on the date.
            data: dailyEnergyChartData.data.map((trace, index) => ({
                ...trace,
                offset: index === 0 ? -BAR_WIDTH_RATIO : 0,
                width: BAR_WIDTH_RATIO
            })),
            layout: {
                ...dailyEnergyChartData.layout,
                dragmode: false,
                height: CHART_HEIGHT,
                width: plotWidth,
                xaxis: {
                    ...dailyEnergyChartData.layout.xaxis,
                    categoryarray: dailyEnergyChartData.data[0]?.x ?? [],
                    categoryorder: "array",
                    dtick: 1
                },
                yaxis: {
                    ...dailyEnergyChartData.layout.yaxis,
                    automargin: true,
                    gridcolor: "#D9D9D9",
                    // 10px gap between the y-axis tick labels and the plotting area (Plotly SVG
                    // property — there is no Tailwind hook for spacing inside the chart).
                    ticklabelstandoff: 10,
                    zerolinecolor: "#D9D9D9"
                }
            }
        }
        : null;
    let energyChartContent;
    if (isLoading) {
        energyChartContent = (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", "data-test": "energy-use-history-loading", children: _jsx(CatLoadingIndicator, {}) }));
    }
    else if (isError) {
        energyChartContent = (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", "data-test": "energy-use-history-error", children: _jsx(APIError, { refreshHandler: () => refetchUsageHistoryDetails() }) }));
    }
    else if (modifiedChartData) {
        const chartWrapperClass = shouldScroll
            ? "w-full justify-center items-center overflow-x-auto [scrollbar-width:thin] [scrollbar-color:#cccccc_#f2f2f2] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:bg-[#f2f2f2] [&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#f2f2f2] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#cccccc]"
            : "w-full justify-center items-center";
        energyChartContent = (_jsx("div", { className: chartWrapperClass, "data-test": "energy-use-chart-wrapper", children: _jsx(BarChart, { config: modifiedChartData.config, data: modifiedChartData.data, layout: modifiedChartData.layout }) }));
    }
    else {
        energyChartContent = (_jsx("div", { className: "flex justify-center items-center h-full w-full min-h-[20vh]", "data-test": "energy-use-history-no-data", children: emptyContent ?? "No data" }));
    }
    return (_jsx("div", { className: "flex flex-col w-full h-[452px] rounded-[4px] border border-[#CCCCCC] pt-4 pr-5 pb-4 pl-5 overflow-hidden", "data-test": "energy-use-history-container", children: _jsxs("div", { className: "flex flex-col gap-0 flex-1 min-h-0", "data-test": "energy-use-history-header", children: [_jsx("ol", { className: "flex flex-shrink-0", children: _jsxs("li", { className: "mr-auto", children: [_jsx("div", { className: "inline-block mb-4 pb-2 border-b-[3px] border-[#FFCD11]", children: _jsx("span", { className: "text-[20px] leading-[28px] font-bold text-[#393D41]", "data-test": "energy-use-history-title", children: "Energy Use History" }) }), _jsxs("div", { className: "text-[16px] font-semibold leading-[24px] text-[#666565]", "data-test": "energy-use-total-discharge", children: ["Total Discharge:", " ", _jsxs("span", { className: "font-['Roboto_Condensed',sans-serif] text-[20px] font-bold leading-[28px] text-[#000000]", children: [Math.round(totalDischarge).toLocaleString(), " kWh"] })] })] }) }), !isLoading && dailyEnergyChartData ? (_jsx("div", { className: "relative -top-[5px] z-10", children: _jsx(EnergyUseLegend, {}) })) : null, _jsx("div", { className: "w-full flex-1 min-h-0 overflow-x-hidden flex flex-col justify-end", "data-test": "energy-use-history-barchart", id: "energy-use-history-chart", ref: chartAreaRef, children: energyChartContent })] }) }));
};
export default EnergyUseHistory;
